<template>
  <div class="sc-message">
    <div class="sc-message--content" :class="{
        system: message.type === 'system',
        typing: message.type === 'typing',
        sent: message.direction === 'sent' && message.type !== 'system' && message.type !== 'typing',
        received: message.direction === 'received' && message.type !== 'system' && message.type !== 'typing',
        carousel: isCarouselMessage
      }"
      :style="messageContentStyle"
    >
      <slot
        v-if="message.direction === 'received' && chatImageUrl"
        name="user-avatar"
        :message="message"
        :user="user">
          <div v-if="message.type !== 'system' && message.type !== 'typing'" :title="authorName" class="sc-message--avatar" :style="{
            backgroundImage: `url(${chatImageUrl})`
          }"></div>
      </slot>

      <div class="sc-message--body">
        <TextMessage
          :class="messageClass"
          v-if="message.type === 'text'"
          :message="message"
          :messageColors="determineMessageColors()"
          :messageStyling="messageStyling"
          @remove="$emit('remove')"
        >
          <template v-slot:default="scopedProps">
            <slot name="text-message-body" :message="scopedProps.message" :messageText="scopedProps.messageText"
                  :messageColors="scopedProps.messageColors" :me="scopedProps.me">
            </slot>
          </template>

          <template v-slot:text-message-toolbox="scopedProps">
            <slot name="text-message-toolbox" :message="scopedProps.message" :me="scopedProps.me">
            </slot>
          </template>

          <template #label v-if="bubbleMetadata.type === 'validation'">
            <p class="bubble-label">{{ bubbleMetadata.field }}</p>
          </template>
        </TextMessage>
        <EmojiMessage v-else-if="message.type === 'emoji'" :data="message.data" />
        <FileMessage v-else-if="message.type === 'file'" :data="message.data" :messageColors="colors" :direction="message.direction" :class="messageClass" />
        <ImageMessage v-else-if="message.type === 'image'" :data="message.data" :class="messageClass" />
        <TypingMessage v-else-if="message.type === 'typing'" :text="(message.data && message.data.text) || ''" :showText="showTypingText" />
        <SystemMessage v-else-if="message.type === 'system'" :data="message.data">
            <slot name="system-message-body" :message="message.data">
            </slot>
        </SystemMessage>
        <ProductCards v-else-if="isProductCardsMessage" :data="message.data" :messageColors="carouselMessageColors()" :productLayout="productLayout" :productDetails="productDetails" @buttonPressed="triggerButtonPressed" :class="messageClass" />
        <CarouselMessage v-else-if="isCarouselMessage" :data="message.data" :messageColors="carouselMessageColors()" @buttonPressed="triggerButtonPressed" :class="messageClass" />
        <StructureMessage v-else-if="message.type === 'structure'" :data="message.data" :messageColors="determineMessageColorsStructure()" @buttonPressed="triggerButtonPressed" :class="messageClass" />

        <Suggestions v-if="isLastMessage && message.suggestions" :suggestions="message.suggestions" v-on:sendSuggestion="triggerQuickReplyPressed" :colors="colors" />

        <div
          v-if="isAiGenerated || citations.length || showFeedback"
          class="sc-message-footer"
          :style="messageFooterStyle"
        >
          <div v-if="isAiGenerated || citations.length" class="sc-message-footer-badges">
            <AIGeneratedBadge v-if="isAiGenerated" />
            <CitationsBadge v-if="citations.length" :citations="citations" :colors="colors" />
          </div>

          <MessageFeedback
            v-if="showFeedback"
            :messageId="feedbackMessageId"
            :interactive="feedbackInteractive"
            :storageType="storageType"
            :sessionId="sessionId"
            :onMessageFeedback="onMessageFeedback"
            :style="{ marginRight: colors.receivedMessage.marginRight }"
          />
        </div>
      </div>
    </div>

    <div v-if="message.direction === 'sent' && message.error" class="sc-message-error">
      {{message.error}}
    </div>
  </div>
</template>

<script>
import TextMessage from './messages/TextMessage.vue'
import FileMessage from './messages/FileMessage.vue'
import EmojiMessage from './messages/EmojiMessage.vue'
import TypingMessage from './messages/TypingMessage.vue'
import SystemMessage from './messages/SystemMessage.vue'
import CarouselMessage from './messages/CarouselMessage.vue'
import ProductCards from './messages/ProductCards.vue'
import StructureMessage from './messages/StructureMessage.vue'
import ImageMessage from './messages/ImageMessage.vue'
import Suggestions from './Suggestions.vue'
import MessageFeedback from './MessageFeedback.vue'
import AIGeneratedBadge from './AIGeneratedBadge.vue'
import CitationsBadge from './CitationsBadge.vue'
import { Event, EventBus } from '@/util/event-bus'

export default {
  components: {
    TextMessage,
    FileMessage,
    EmojiMessage,
    TypingMessage,
    SystemMessage,
    CarouselMessage,
    ProductCards,
    StructureMessage,
    ImageMessage,
    Suggestions,
    MessageFeedback,
    AIGeneratedBadge,
    CitationsBadge
  },
  props: {
    message: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: true
    },
    isLastMessage: {
      type: Boolean,
      default: false
    },
    isAiGenerated: {
      type: Boolean,
      default: false
    },
    citations: {
      type: Array,
      default: () => []
    },
    showFeedback: {
      type: Boolean,
      default: false
    },
    feedbackInteractive: {
      type: Boolean,
      default: false
    },
    feedbackMessageId: {
      type: [String, Number],
      default: null
    },
    onButtonPressed: {
      type: Function,
      required: true
    },
    onMessageFeedback: {
      type: Function,
      default: () => {}
    },
    storageType: {
      type: String,
      default: 'local'
    },
    sessionId: {
      type: String,
      default: null
    },
    messageStyling: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      required: true
    },
    messageLabel: {
      type: String,
      default: ''
    },
    showTypingText: {
      type: Boolean,
      default: true
    }
  },

  methods: {
    carouselMessageColors () {
      return {
        '--button-text': this.colors.sentMessage.carouselButton.text,
        '--button-text-hover': this.colors.sentMessage.carouselButton.textHover,
        '--button-bg-hover': this.colors.sentMessage.carouselButton.bgHover
      }
    },
    sentColorsStyle (isStruct = false) {
      const styles = {
        color: this.colors.sentMessage.text,
        background: isStruct ? this.colors.sentMessage.button.bg : this.colors.sentMessage.bg,
        fontSize: this.colors.messageBubble.fontSize,
        fontWeight: this.colors.messageBubble.fontWeight
      }
      if (isStruct) {
        styles.border = this.colors.sentMessage.button.border
        styles['--button-text'] = this.colors.sentMessage.button.text
        styles['--button-text-hover'] = this.colors.sentMessage.button.textHover
        styles['--button-bg-hover'] = this.colors.sentMessage.button.bgHover
        styles['--button-radius'] = this.colors.sentMessage.button.radius || '6px'
      } else {
        styles.lineHeight = this.colors.messageBubble.lineHeight
        styles.padding = this.colors.messageBubble.paddingText
      }
      return styles
    },
    receivedColorsStyle (isStruct = false) {
      return {
        color: this.colors.receivedMessage.text,
        marginRight: this.colors.receivedMessage.marginRight,
        backgroundColor: this.colors.receivedMessage.bg,
        fontSize: this.colors.messageBubble.fontSize,
        fontWeight: this.colors.messageBubble.fontWeight,
        lineHeight: this.colors.messageBubble.lineHeight,
        padding: isStruct ? this.colors.messageBubble.paddingStruct : this.colors.messageBubble.paddingText,
        opacity: this.colors.receivedMessage.opacity || '100%',
        backdropFilter: this.colors.receivedMessage.backdropFilter || null,
        boxShadow: this.colors.receivedMessage.boxShadow || null
      }
    },
    determineMessageColors () {
      return this.message.direction === 'sent' ? this.sentColorsStyle() : this.receivedColorsStyle()
    },
    determineMessageColorsStructure () {
      return {
        sent: this.sentColorsStyle(true),
        received: this.receivedColorsStyle(true)
      }
    },
    triggerButtonPressed (button) {
      EventBus.$emit(Event.INPUT_HIDE_VALIDATION)
      this.onButtonPressed({
        type: 'button',
        data: button
      })
    },
    triggerQuickReplyPressed (quickReply) {
      this.onButtonPressed({
        type: 'quickReply',
        data: quickReply
      })
    }
  },
  computed: {
    isCarouselMessage () {
      return this.message.type === 'structure' && Array.isArray(this.message.data)
    },
    // PRO-514: a carousel of PRODUCTS, which the engine sends with structured fields (price, labels, sizes) that
    // a generic carousel has no way to render. Detected off the cards themselves rather than off the layout
    // metadata, so a bot's own hand-built carousel keeps the renderer it has always had.
    isProductCardsMessage () {
      // The product fields cannot live in the payload — the API rejects any element key outside
      // title/text/image_url/item_url/buttons — so PRO-550 has the engine send them as `details` on that same
      // element, and that is what marks this as a product carousel. A bot's own hand-built carousel has no such
      // `details` and keeps the renderer it has always had.
      return this.isCarouselMessage && (this.productDetails || []).some(detail => detail && detail.product)
    },
    productDetails () {
      if (!Array.isArray(this.message.data)) return null
      const details = this.message.data.map(card => (card && card.details) || null)
      return details.some(Boolean) ? details : null
    },
    // Which layout the engine chose for this answer. Unknown or absent falls back to the carousel inside the
    // component, so an older engine renders exactly as before.
    productLayout () {
      return this.message.metadata?.product_layout || null
    },
    authorName () {
      return this.user && this.user.name
    },
    chatImageUrl () {
      return (this.user && this.user.imageUrl) || this.chatIcon
    },
    messageClass () {
      return 'sc-message-bubble ' + (this.message.direction || '')
    },
    bubbleMetadata () {
      return this.message.metadata?.bubble || {}
    },
    messageContentStyle () {
      if (this.message.type === 'system' || this.isCarouselMessage) return {}
      return { width: this.colors.messageBubble.width }
    },
    // Carousels skip the bubble-column width so cards can use the full row. The footer still
    // uses that column width so thumbs line up with the preceding text bubble.
    messageFooterStyle () {
      if (!this.isCarouselMessage) return {}
      return { width: this.colors.messageBubble.width }
    }
  }
}
</script>
